/** How dnclient gets onto a Linux host: a package manager, or a directly downloaded binary. */
export type LinuxInstallMethod = 'apt' | 'rpm' | 'aur' | 'binary';
export type PackageManager = Exclude<LinuxInstallMethod, 'binary'>;

/**
 * The apt/rpm steps are the canonical ones published at <https://dl.defined.net/stable/index.html> —
 * keep these in sync with that page; aur is the community package at
 * <https://aur.archlinux.org/packages/dnclient>. Unlike the binary download, the packages put
 * `dnclient` on the PATH.
 */
export const PACKAGE_INSTALL_COMMANDS: Record<PackageManager, string[]> = {
  apt: [
    'sudo curl -fsSL https://dl.defined.net/gpg.asc -o /etc/apt/keyrings/defined-net.asc',
    'echo "deb [signed-by=/etc/apt/keyrings/defined-net.asc] https://dl.defined.net/stable/apt stable main" | sudo tee /etc/apt/sources.list.d/defined-net.list > /dev/null',
    'sudo apt-get update',
    'sudo apt-get install dnclient',
    'sudo systemctl enable --now dnclient',
  ],
  rpm: [
    'sudo dnf config-manager --add-repo https://dl.defined.net/stable/rpm/defined-net.repo',
    'sudo dnf install dnclient',
    'sudo systemctl enable --now dnclient',
  ],
  aur: ['yay -S dnclient', 'sudo systemctl enable --now dnclient'],
};

/** Cards for the enroll page's install-method picker. */
export const INSTALL_METHODS: Array<{ key: LinuxInstallMethod; title: string; description: string }> = [
  { key: 'apt', title: 'APT repository', description: 'Debian, Ubuntu, Mint, Pop!_OS' },
  { key: 'rpm', title: 'RPM repository', description: 'RHEL, Fedora, Rocky, Alma, Amazon Linux' },
  { key: 'aur', title: 'AUR', description: 'Arch, Manjaro, EndeavourOS' },
  { key: 'binary', title: 'Binary download', description: 'Any distribution' },
];

/** The same package repositories with select-friendly labels, for the get-started flow's install select. */
export const PACKAGE_OPTIONS: Array<{ key: PackageManager; label: string }> = [
  { key: 'apt', label: 'Debian, Ubuntu & derivatives (apt)' },
  { key: 'rpm', label: 'RHEL, Fedora & derivatives (dnf)' },
  { key: 'aur', label: 'Arch & derivatives (AUR)' },
];

/** The arches surfaced in the "Popular" group of the Linux binary pickers. */
export const ARCHITECTURES_POPULAR = ['linux-386', 'linux-amd64', 'linux-arm64'];

/**
 * The same install as a single `&&`-joined chain, for one-copy-paste layouts. Every line ends in
 * `&& \` — the chain is incomplete until the caller appends a final command (the enroll command).
 */
export function chainedPackageInstall(manager: PackageManager): string[] {
  return PACKAGE_INSTALL_COMMANDS[manager].map((command) => `${command} && \\`);
}
